import { getOssToken } from '@/api/index';
import * as OSS from 'ali-oss';
import md5 from 'md5-js/md5';
import dayjs from 'dayjs';
import Vue from 'vue';

const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;

/**
 * 根据文件大小获取最优的分片上传配置
 * @param {number} fileSize 文件大小（字节）
 * @returns {object} 包含 partSize 和 parallel 的配置对象
 */
function getUploadOptions(fileSize) {
  if (fileSize < 5 * MB) {
    return { partSize: 1 * MB, parallel: 3 };
  } else if (fileSize < 50 * MB) {
    return { partSize: 2 * MB, parallel: 3 };
  } else if (fileSize < 100 * MB) {
    return { partSize: 5 * MB, parallel: 5 };
  } else if (fileSize < 500 * MB) {
    return { partSize: 10 * MB, parallel: 5 };
  } else if (fileSize < 1 * GB) {
    return { partSize: 20 * MB, parallel: 8 };
  } else {
    const partSize = Math.min(Math.ceil(fileSize / 10000), 5 * GB);
    return { partSize: Math.max(partSize, 100 * KB), parallel: Math.min(Math.ceil(fileSize / (50 * MB)), 10) };
  }
}

/**
 * 获取图片尺寸
 * @param {File} file 图片文件对象
 * @returns {Promise<{width: number, height: number}>} 图片尺寸
 */
function getImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.width,
        height: img.height
      });
    };

    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };

    img.src = url;
  });
}

/**
 * 普通上传至OSS（适用于小文件）
 * @param {File} file 文件对象
 * @param {string} dir 文件保存目录（例如 'uploads/')
 * @param {boolean} is_size_limit 是否限制图片尺寸
 * @returns {Promise<string>} 返回最终文件URL
 */
export async function uploadSmallFileToOSS(file, dir = '', is_size_limit = false) {
  console.log('file-type', file.type);
  // 图片尺寸校验逻辑
  if (file.type.startsWith('image/') && is_size_limit) {
    try {
      const { width, height } = await getImageDimensions(file);
      const totalPixels = width * height;
      console.log('width', width, 'height', height, 'totalPixels', totalPixels);

      // 校验宽度不能超过 4096px
      if (width > 4096) {
        throw new Error('Image width exceeds limit');
      }

      // 校验总像素不能超过 9437184 (约等于 4096*2304 或 3072*3072)
      if (totalPixels > 9437184) {
        throw new Error('Image total pixels exceed limit');
      }
    } catch (e) {
      const errorMsg = (e && e.message) ? e.message : String(e);
      // 如果是因为校验失败抛出的错误，直接中断上传
      if (errorMsg.includes('limit') || errorMsg.endsWith('limit')) {
        const customError = new Error('IMAGE_SIZE_LIMIT_EXCEEDED');
        customError.code = 'IMAGE_SIZE_LIMIT';
        return Promise.reject(customError);
      }
      // 如果是读取图片失败，可以选择警告但继续上传，或者中断
      console.warn('无法读取图片尺寸，跳过尺寸校验', e);
    }
  }
  try {
    Vue.prototype.$globalLoading.show('文件上传中...', true);

    // 获取OSS上传配置
    const date = dayjs().format('YYYYMMDD');
    const { data: ossConfig } = await getOssToken({ dir: `${dir}/${date}` });
    console.log('ossConfig', ossConfig);

    // 生成文件存储路径
    const nameHash = await getFileHash(file);
    const ext = file.name.split('.').pop();
    const fileName = `${nameHash}.${ext}`;
    const key = `${ossConfig.dir}/${fileName}`;

    // 获取分片上传配置
    const { partSize, parallel } = getUploadOptions(file.size);

    // 创建OSS客户端
    const client = new OSS({
      region: ossConfig.region,
      endpoint: 'https://' + ossConfig.endpoint,
      accessKeyId: ossConfig.accessKeyId,
      accessKeySecret: ossConfig.accessKeySecret,
      stsToken: ossConfig.securityToken,
      bucket: ossConfig.bucket,
      secure: process.env.NODE_ENV !== 'development',
    });

    // 执行上传（使用分片上传方式）
    await client.multipartUpload(key, file, {
      partSize,
      parallel,
      headers: {
        'Content-Disposition': `attachment; filename="${encodeURIComponent(file.name)}"`,
      },
      progress: (p) => {
        const percent = Math.round(p * 100);
        Vue.prototype.$globalLoading.updateProgress(percent);
      },
    });

    Vue.prototype.$globalLoading.hide();

    // 返回文件URL
    return `${ossConfig.oss_url}/${key}`;
  } catch (error) {
    Vue.prototype.$globalLoading.hide();
    console.error('上传失败:', error);
    throw error;
  }
}

/**
 * 分片上传至OSS（适用于大文件）
 * @param {File} file 文件对象
 * @param {string} dir 文件保存目录
 * @returns {Promise<string>} 返回最终文件URL
 */
export async function uploadLargeFileToOSS(file, dir = '') {
  try {
    Vue.prototype.$globalLoading.show('文件上传中...', true);

    // 获取OSS上传配置
    const date = dayjs().format('YYYYMMDD');
    const { data: ossConfig } = await getOssToken({ dir: `${dir}/${date}` });
    console.log('ossConfig', ossConfig);

    // 生成文件存储路径
    const nameHash = await getFileHash(file);
    const ext = file.name.split('.').pop();
    const fileName = `${nameHash}.${ext}`;
    const key = `${ossConfig.dir}/${fileName}`;

    // 获取分片上传配置
    const { partSize, parallel } = getUploadOptions(file.size);

    // 创建OSS客户端
    const client = new OSS({
      region: ossConfig.region,
      endpoint: 'https://' + ossConfig.endpoint,
      accessKeyId: ossConfig.accessKeyId,
      accessKeySecret: ossConfig.accessKeySecret,
      stsToken: ossConfig.securityToken,
      bucket: ossConfig.bucket,
      secure: process.env.NODE_ENV !== 'development',
    });

    // 执行分片上传
    await client.multipartUpload(key, file, {
      partSize,
      parallel,
      headers: {
        'Content-Disposition': `attachment; filename="${encodeURIComponent(file.name)}"`,
      },
      progress: (p) => {
        const percent = Math.round(p * 100);
        Vue.prototype.$globalLoading.updateProgress(percent);
      },
    });

    Vue.prototype.$globalLoading.hide();

    // 返回文件URL
    return `${ossConfig.oss_url}/${key}`;
  } catch (error) {
    Vue.prototype.$globalLoading.hide();
    console.error('上传失败:', error);
    throw error;
  }
}

/**
 * 自动判断上传方式（普通 or 分片）
 * @param {File} file 文件对象
 * @param {string} dir 文件保存目录
 * @param {boolean} is_size_limit 是否限制图片尺寸
 * @param {number} [threshold=5] 判断阈值（单位：MB）
 * @returns {Promise<string>}
 */
export async function uploadFileToOSS(file, dir = '', is_size_limit = false, threshold = 5) {
  //>暂时只保留小文件直传
  return await uploadSmallFileToOSS(file, dir, is_size_limit);
  // const fileSizeInMB = file.size / (1024 * 1024); // 文件大小转换为 MB
  // if (fileSizeInMB < threshold) {
  //   return await uploadSmallFileToOSS(file, dir);
  // } else {
  //   return await uploadLargeFileToOSS(file, dir);
  // }
}

/**
 * 获取文件哈希值（用于生成唯一文件名）
 * @param {File} file 文件对象
 * @returns {Promise<string>} 文件哈希值（SHA-1或MD5）
 */
async function getFileHash(file) {
  // 首先尝试使用Web Crypto API (现代浏览器)
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    try {
      const buffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-1', buffer);

      // 将 ArrayBuffer 转换为十六进制字符串
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      return hashHex;
    } catch (error) {
      console.warn('Web Crypto API failed, falling back to MD5', error);
    }
  }

  // 降级方案：使用MD5 (兼容旧浏览器)
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = function (e) {
      try {
        // 使用项目中已有的md5-js包
        const hash = md5(e.target.result);
        resolve(hash);
      } catch (error) {
        // 如果md5-js也失败了，使用简单的备用方案
        // 这里使用一个简单的方法生成基于文件名和大小的"hash"
        const fallbackHash = btoa(file.name + file.size).replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
        resolve(fallbackHash.substring(0, 40)); // SHA-1长度为40字符
      }
    };
    reader.onerror = function () {
      // 文件读取出错时的备用方案
      const fallbackHash = btoa(file.name + file.size).replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      resolve(fallbackHash.substring(0, 40));
    };
    reader.readAsBinaryString(file);
  });
}